import React, { useCallback, useMemo, useState } from 'react';
import { supabase } from '../../supabaseClient';
import Modal from '../Modal';
import {
  FEEDBACK_QUESTIONS, FEEDBACK_KEYS, answerNeedsNote, isReportable,
} from '../../lib/briefFeedback';
import { escalateSafetyIncident } from '../../lib/safetyIncident';

// Post-event feedback capture (brief post-event-feedback §3/§4). Four questions
// + an optional internal note. NOTHING is mandatory — Skip is a real button,
// partial responses store as partial. A negative answer PROMPTS for a note (it
// doesn't block submit). Selecting Reportable warns what will happen BEFORE
// sending, then on submit raises a safety incident.
//
// This component handles capture + the incident record. The escalation EMAIL
// (compliance-alert recipients) and the Safety-recognition pause are wired in a
// following phase.

const TONE_ON = {
  good: 'border-[#2F6E14] bg-[#F5FAEE] text-[#2F6E14]',
  warn: 'border-[#D9A93C] bg-[#FBF0DC] text-[#9A6208]',
  bad:  'border-[#D8341A] bg-[#FCEAE6] text-[#D8341A]',
};

const Spinner = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" className="animate-spin">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" fill="none" strokeOpacity="0.25" />
    <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" />
  </svg>
);

export default function BriefFeedbackModal({ brief, supplierId, supplierName, feedback, currentUser, onSuccess, onClose }) {
  const [answers, setAnswers] = useState(() => ({
    delivered: feedback?.delivered || null,
    on_time: feedback?.on_time || null,
    safety: feedback?.safety || null,
    use_again: feedback?.use_again || null,
  }));
  const [note, setNote] = useState(feedback?.note || '');
  const [state, setState] = useState('input'); // input | submitting | error
  const [errorMessage, setErrorMessage] = useState(null);

  const submitting = state === 'submitting';
  const isAmend = !!feedback?.completed_at;

  const anyNeedsNote = useMemo(
    () => FEEDBACK_KEYS.some(k => answerNeedsNote(k, answers[k])),
    [answers],
  );
  const reportable = isReportable(answers.safety);
  const anyAnswered = FEEDBACK_KEYS.some(k => answers[k]);
  const allAnswered = FEEDBACK_KEYS.every(k => answers[k]);

  const setAnswer = (key, value) => setAnswers(prev => ({ ...prev, [key]: prev[key] === value ? null : value }));

  const submit = useCallback(async (mode) => {
    // mode: 'send' | 'skip'
    if (submitting || !brief?.id || !supplierId) return;
    if (mode === 'skip') { onClose?.(); return; }
    setState('submitting'); setErrorMessage(null);
    try {
      const nowIso = new Date().toISOString();
      const row = {
        brief_id: brief.id,
        supplier_id: supplierId,
        requested_at: feedback?.requested_at || nowIso,
        completed_at: nowIso,
        answered_by_user_id: currentUser?.id || null,
        delivered: answers.delivered,
        on_time: answers.on_time,
        safety: answers.safety,
        use_again: answers.use_again,
        note: note.trim() || null,
        status: allAnswered ? 'complete' : 'partial',
        updated_at: nowIso,
        ...(isAmend ? { amended_at: nowIso, amended_by_user_id: currentUser?.id || null } : {}),
      };
      const { data: saved, error: upErr } = await supabase
        .from('brief_feedback')
        .upsert(row, { onConflict: 'brief_id,supplier_id' })
        .select('id')
        .single();
      if (upErr) throw new Error(upErr.message);

      // Reportable → raise a safety incident (duty attached, brief §4). Only if
      // one isn't already open for this feedback, so an amend doesn't duplicate.
      if (reportable) {
        const { data: existing } = await supabase
          .from('supplier_incidents')
          .select('id')
          .eq('feedback_id', saved.id)
          .is('closed_at', null)
          .limit(1);
        if (!existing || existing.length === 0) {
          const { error: incErr } = await supabase.from('supplier_incidents').insert({
            supplier_id: supplierId,
            brief_id: brief.id,
            feedback_id: saved.id,
            severity: 'reportable',
            note: note.trim() || null,
            raised_by_user_id: currentUser?.id || null,
          });
          if (incErr) throw new Error(incErr.message);
          // Escalate immediately — best-effort, never blocks the save.
          escalateSafetyIncident({
            supplierId,
            supplierName,
            briefId: brief.id,
            briefName: brief.brief_name,
            projectNumber: brief.fusion_project_number,
            note: note.trim() || null,
            raisedByUserId: currentUser?.id || null,
          }).catch(() => {});
        }
      }

      onSuccess?.({ reportable });
    } catch (e) {
      setErrorMessage(e.message || 'Could not save. Try again.');
      setState('error');
    }
  }, [submitting, brief?.id, supplierId, feedback?.requested_at, currentUser?.id, answers, note, allAnswered, isAmend, reportable, onClose, onSuccess]);

  const safeClose = () => { if (!submitting) onClose?.(); };

  return (
    <Modal open onClose={safeClose} maxWidth={560} stripe={false} ariaLabel={`Feedback — ${supplierName || 'supplier'}`}>
      <Modal.Header
        eyebrow={isAmend ? 'Amend feedback' : 'How did it go?'}
        title={supplierName || 'Supplier'}
        sub={brief?.brief_name}
        onClose={submitting ? undefined : safeClose}
      />
      <Modal.Body>
        <p className="font-['Metropolis'] text-[12px] text-[#51534A] mb-4 leading-snug">
          Takes about a minute. Nothing's required — leave anything blank, or skip entirely.
        </p>

        {FEEDBACK_QUESTIONS.map(q => (
          <div key={q.key} className="mb-4" role="radiogroup" aria-label={q.label}>
            <p className="font-['Metropolis'] text-[14px] font-semibold text-ink mb-2">{q.label}</p>
            <div className="flex flex-wrap gap-2">
              {q.options.map(o => {
                const on = answers[q.key] === o.value;
                return (
                  <button
                    key={o.value}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    disabled={submitting}
                    onClick={() => setAnswer(q.key, o.value)}
                    className={`font-['Metropolis'] text-[13px] font-semibold px-4 py-2 rounded-full border transition-colors disabled:opacity-50 ${on ? TONE_ON[o.tone] : 'border-line text-fusion-mid-grey bg-white hover:border-[#C3C9AF]'}`}>
                    {o.label}
                  </button>
                );
              })}
            </div>
            {q.key === 'safety' && reportable && (
              <div role="alert" className="mt-2.5 flex gap-2.5 rounded-lg px-3 py-2.5" style={{ backgroundColor: '#FCEAE6', border: '1px solid #F2C9C0', color: '#8E1F0A' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, marginTop: 2 }} aria-hidden="true"><path d="M12 9v4M12 17v.01M10.3 3.9 2.4 18a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" strokeLinecap="round" /></svg>
                <div className="font-['Metropolis'] text-[12.5px] leading-snug">
                  <b className="block font-bold mb-0.5">This raises a safety incident on submit.</b>
                  An incident is recorded against {supplierName || 'the supplier'} and their Safety recognition pauses until someone closes it. It doesn't block them from work on its own — that's a separate decision.
                </div>
              </div>
            )}
          </div>
        ))}

        <div className="mb-1">
          <label htmlFor="feedback-note" className="block font-['Metropolis'] text-[14px] font-medium text-ink-secondary mb-2">
            Anything worth noting? <span className="text-[#9BA08C] text-[12px] font-normal">Optional · internal only</span>
          </label>
          <textarea
            id="feedback-note"
            value={note}
            onChange={e => setNote(e.target.value)}
            disabled={submitting}
            rows={3}
            placeholder="Only you and the team see this."
            className="w-full bg-white border border-line rounded-md px-3 py-2.5 font-['Metropolis'] text-[13px] text-ink placeholder:text-[#9BA08C] focus:outline-none focus:border-[#2F6E14] disabled:opacity-50 resize-y" />
          {anyNeedsNote && !note.trim() && (
            <p className="mt-2 flex gap-1.5 items-start font-['Metropolis'] text-[12px] text-[#9A6208]">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, marginTop: 2 }} aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8v.01" strokeLinecap="round" /></svg>
              A line of context here would help whoever picks suppliers next time. Still optional.
            </p>
          )}
        </div>

        {state === 'error' && errorMessage && (
          <div role="alert" className="mt-4 rounded-md px-3 py-2.5 font-['Metropolis'] text-[12px]" style={{ backgroundColor: 'rgba(224,41,10,0.08)', border: '1px solid rgba(224,41,10,0.35)', color: '#E0290A' }}>
            Couldn't save. {errorMessage}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <span className="font-['Metropolis'] text-[11px] text-[#51534A]">You can leave anything blank.</span>
          <div className="flex items-center gap-3">
            {!isAmend && (
              <button type="button" onClick={() => submit('skip')} disabled={submitting}
                className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.15em] text-[#51534A] hover:text-fusion-mid-grey bg-transparent border-0 cursor-pointer disabled:opacity-50">Skip</button>
            )}
            <button type="button" onClick={() => submit('send')} disabled={submitting || (!anyAnswered && !note.trim())}
              className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.15em] text-white bg-[#2F6E14] border border-[#2F6E14] rounded-full px-5 py-2 inline-flex items-center gap-2 transition-[filter,opacity] duration-150 hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed">
              {/* SAVE, not send. Nothing leaves the building — this records an
                  internal assessment of how the supplier did. "Send" implies it
                  goes to them, and the sharing of feedback is a separate,
                  deliberate act elsewhere. The spinner already said "Saving…",
                  so the button was contradicting itself mid-click. */}
              {submitting ? (<><Spinner /><span>Saving…</span></>) : <span>{isAmend ? 'Save changes' : 'Save feedback'}</span>}
            </button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
